'use client'

import { Building2, CircleUser, LogOut, Shield, type LucideIcon } from 'lucide-react'
import Link from 'next/link'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Progress } from '@/components/ui/progress'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { LogoMark } from './logo'
import { portalNavTriggerClassName } from './portal-nav-styles'

// Placeholder profile — will be replaced by the authenticated member from the backend.
const profile = {
  initials: 'OA',
  name: 'Omar Almasry',
  role: 'UX Developer',
  company: 'Ensemble Travel (CA)',
  progress: 40,
}

const menuItems: { label: string; icon: LucideIcon; href?: string }[] = [
  { label: 'Profile', icon: CircleUser },
  { label: 'Partner Profile', icon: Building2, href: '/partner-profile' },
  { label: 'Admin', icon: Shield },
]

export function ProfileMenu() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className={`${portalNavTriggerClassName} focus-visible:ring-ring/50 focus-visible:ring-offset-background ml-1 flex size-11 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:ml-4`}
        >
          <Avatar className="size-10">
            <AvatarFallback className="group-hover/header-nav:bg-interaction-neutral group-hover/header-nav:text-foreground group-focus-visible/header-nav:bg-interaction-neutral group-focus-visible/header-nav:text-foreground group-data-[state=open]/header-nav:bg-sidebar-accent group-data-[state=open]/header-nav:text-sidebar-primary text-sm font-semibold transition-colors">
              {profile.initials}
            </AvatarFallback>
          </Avatar>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={16}
        className="w-72 max-w-[calc(100vw-2rem)] p-0"
      >
        {/* Identity */}
        <div className="flex items-start gap-3 p-4">
          <LogoMark className="text-brand-maroon h-8 w-auto shrink-0" />
          <div className="min-w-0">
            <p className="leading-tight font-semibold">{profile.name}</p>
            <p className="text-muted-foreground text-sm">{profile.role}</p>
            <button type="button" className="text-link link-hover-underline text-sm font-medium">
              {profile.company}
            </button>
          </div>
        </div>

        {/* Profile progress */}
        <div className="border-t p-4">
          <p className="text-sm font-semibold">Your Profile Progress</p>
          <p className="text-muted-foreground mt-0.5 text-sm">
            Complete your profile to get verified
          </p>
          <div className="mt-3 flex items-center gap-3">
            <Progress value={profile.progress} className="flex-1" />
            <span className="text-muted-foreground shrink-0 text-sm font-medium">
              {profile.progress}%
            </span>
          </div>
        </div>

        {/* Links — the same neutral hover wash as inactive sidebar links. */}
        <div className="border-t p-2">
          {menuItems.map(({ label, icon: Icon, href }) => {
            const itemClass =
              'group hover:bg-interaction-neutral hover:text-foreground focus-visible:bg-interaction-neutral focus-visible:text-foreground focus-visible:ring-ring/50 flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors outline-none focus-visible:ring-2'
            const iconClass =
              'text-muted-foreground group-hover:text-foreground group-focus-visible:text-foreground size-5 shrink-0 transition-colors'

            return href ? (
              <Link key={label} href={href} className={itemClass}>
                <Icon className={iconClass} />
                {label}
              </Link>
            ) : (
              <button key={label} type="button" className={itemClass}>
                <Icon className={iconClass} />
                {label}
              </button>
            )
          })}
        </div>

        {/* Logout */}
        <div className="border-t p-2">
          <button
            type="button"
            className="text-destructive-strong hover:bg-destructive/10 flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm font-semibold transition-colors"
          >
            <LogOut className="size-5 shrink-0" />
            Logout
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
